import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
    ApplyClassificationRequestSchema,
    ApplyClassificationSuggestionResponseSchema,
    ApplyClusterRequestSchema,
    ApplyClusterResponseSchema,
    BatchReassignTrackRequestSchema,
    BatchReassignTrackResponseSchema,
    GenerateClassificationSuggestionResponseSchema,
    GenerateSuggestionRequestSchema,
    GetClassificationSuggestionResponseSchema,
    GetSuggestionStatsRequestSchema,
    ListClassificationSuggestionsResponseSchema,
    ListEmailsRequestSchema,
    ListEmailsResponseSchema,
    ListSuggestionsRequestSchema,
    ReanalyzeAllSuggestionsResponseSchema,
    ReanalyzeClassificationSuggestionResponseSchema,
    ReclassifyAllEmailsResponseSchema,
    ReassignTrackRequestSchema,
    ReassignTrackResponseSchema,
    RejectClassificationSuggestionResponseSchema,
    SuggestionFeedbackRequestSchema,
    SuggestionFeedbackResponseSchema,
    SuggestionStatsResponseSchema,
} from '../schemas/classify';
import {
    BatchApplyRequestSchema,
    BatchApplyResponseSchema,
    GetActiveApplyJobResponseSchema,
    GetApplyJobResponseSchema,
} from '../schemas/apply-job';
import { ClassificationRejectSuggestionRequestSchema, ClassificationExcludeSuggestionRequestSchema, ClassificationExcludeSuggestionResponseSchema, ClassificationExcludeConflictResponseSchema } from '../schemas/suggestions';
import { SuggestionDebugPayloadSchema } from '../schemas/suggestion-debug';
import { defaultErrorResponses } from './errors';

const c = initContract();

/**
 * Smart Track Classification Contract.
 * Pre-LLM evaluation applies deterministic DB rules. Unmatched email continues
 * through LLM advisory and authoritative Post-LLM resolution.
 * All endpoints require authMiddleware. Suggestions expire in 7 days.
 */
export const classifyContract = c.router({
  /**
   * Fetches emails from database or live Gmail for preview in suggestion generator modal.
   * Gets: limit (1-100, default: 50), source ('db'|'gmail', default: 'db') from query.
   * Returns: { source, count, emails[] } with message_id, subject, from_address, snippet, labels.
   * Side Effects: None (read-only).
   */
  listEmails: {
    method: 'GET',
    path: '/v1/classify/emails/unread',
    query: ListEmailsRequestSchema,
    responses: {
      200: ListEmailsResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Fetch unread emails for preview',
      description: 'Lists unread emails from database cache or live Gmail for use in suggestion generation modal.',
    },
  },
  /**
   * Classifies a single email through Pre-LLM evaluation, optional LLM advisory, and Post-LLM resolution.
   * Gets: message_id (Gmail ID), force_llm (skip Pre-LLM evaluation), min_confidence (0-1 threshold) in body.
   * Returns: suggestion_id, track ('Attention'|'Archive'|'Trash'), labels[], actions{}, confidence (0.5-1.0), rationale, phase1_match, llm_used.
   * Side Effects: Creates suggestion record; may call Azure OpenAI for LLM advisory.
   * Error Codes: CLASSIFICATION_FAILED, INVALID_MESSAGE_ID, CONFIDENCE_TOO_LOW.
   */
  generateSuggestion: {
    method: 'POST',
    path: '/v1/classify/suggestions/generate',
    body: GenerateSuggestionRequestSchema,
    responses: {
      200: GenerateClassificationSuggestionResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Generate classification suggestion for one message',
      description: 'Classification through Pre-LLM evaluation, optional LLM advisory, and Post-LLM resolution.',
      errorCodes: ['CLASSIFICATION_FAILED', 'INVALID_MESSAGE_ID', 'CONFIDENCE_TOO_LOW'],
    },
  },
  /**
   * Lists classification suggestions with optional filtering by status/track/search/label and pagination.
   * Gets: status ('PENDING'|'APPLIED'|'REJECTED'), track ('Attention'|'Archive'|'Trash'), label (matches labels[0]; pass the "Uncategorized" sentinel for rows with no labels), search (ILIKE match on subject/from_address/snippet), limit (default: 50, max: 100), offset from query.
   * Returns: { suggestions[], total, summary } with id, track, labels, confidence, status, created_at; summary.by_label (nested track -> label -> { count, size_bytes }) is present only when offset === 0.
   * Side Effects: None (read-only).
   */
  listSuggestions: {
    method: 'GET',
    path: '/v1/classify/suggestions',
    query: ListSuggestionsRequestSchema,
    responses: {
      200: ListClassificationSuggestionsResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'List classification suggestions with filtering and search',
      description: 'Paginated list of classification suggestions with status/track/label filtering and full-text search on subject, sender, snippet. summary.by_label carries per-(track,label) counts + reclaimable size when offset === 0.',
    },
  },
  /**
   * Authoritative calculation of suggestion counts, track totals, status breakdowns, and reclaimable sizes.
   * Single source of truth for dashboard, cleanup counters, and preview.
   * Gets: status, track, scanJobId, search, label, confidenceFloor from query.
   * Returns: { total, by_track, by_status, avg_confidence, total_size_bytes, size_by_track, by_label }.
   * Side Effects: None (read-only).
   */
  getSuggestionStats: {
    method: 'GET',
    path: '/v1/classify/suggestions/stats',
    query: GetSuggestionStatsRequestSchema,
    responses: {
      200: SuggestionStatsResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Get authoritative suggestion statistics and totals',
      description: 'Calculates total suggestion counts, track breakdowns, status counts, and reclaimable sizes using exact server-side aggregates.',
    },
  },
  /**
   * Retrieves detailed information for a specific classification suggestion including signals and rationale.
   * Gets: suggestion id from path. Returns: full suggestion with signals (attachments, urgency_level 0-10, is_vip, etc.), confidence, rationale, phase1_match, llm_used, expires_at.
   * Side Effects: None (read-only). May update last_accessed timestamp internally.
   * Error Codes: SUGGESTION_EXPIRED (if past expiration).
   */
  getSuggestion: {
    method: 'GET',
    path: '/v1/classify/suggestions/:id',
    pathParams: z.object({ id: z.string() }),
    responses: {
      200: GetClassificationSuggestionResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Fetch classification suggestion details',
      description: 'Get detailed classification suggestion with signals, rationale, and confidence breakdown.',
      errorCodes: ['SUGGESTION_EXPIRED'],
    },
  },
  /**
   * DEV-ONLY. Returns the complete replay payload behind a suggestion - everything a gold-set
   * fixture needs, so a pasted payload can be turned into a fixture without a Supabase round trip.
   * Gets: suggestion id from path.
   * Returns: the suggestion detail PLUS thread_id, allowlisted raw headers, unfiltered Gmail
   * labels, llm_metadata, decision provenance, and the full sender_insights row.
   * Side Effects: None (read-only).
   * Error Codes: 404 in production - the endpoint is gated off outside development.
   */
  getSuggestionDebug: {
    method: 'GET',
    path: '/v1/classify/suggestions/:id/debug',
    pathParams: z.object({ id: z.string() }),
    responses: {
      200: SuggestionDebugPayloadSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Fetch the dev-only classification replay payload',
      description: 'Dev-only. Full runtime inputs for a suggestion (headers, sender_insights, llm_metadata) used to seed gold-set fixtures. Returns 404 in production.',
    },
  },
  /**
   * Applies classification suggestion by executing Gmail actions (archive/trash/labels) and logging activity.
   * Gets: suggestion id from path, optional labels[] and actions{} overrides in body.
   * Returns: { success: true, operation_id, affected_count }.
   * Side Effects: Updates Gmail (archive=remove INBOX, trash=add TRASH+remove INBOX, apply_labels=add label), creates activity_log entry, updates suggestion status to 'APPLIED'.
   * Chunking: Batches 500 messages per Gmail API call; auto-retries on token expiry.
   * Error Codes: SUGGESTION_EXPIRED, SUGGESTION_ALREADY_APPLIED, INVALID_MESSAGE_ID.
   */
  applySuggestion: {
    method: 'POST',
    path: '/v1/classify/suggestions/:id/apply',
    pathParams: z.object({ id: z.string() }),
    body: ApplyClassificationRequestSchema.optional(),
    responses: {
      200: ApplyClassificationSuggestionResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Apply a classification suggestion',
      description: 'Execute Gmail actions (archive, trash, label) from suggestion with optional action overrides. Logs to activity_logs.',
      errorCodes: ['SUGGESTION_EXPIRED', 'SUGGESTION_ALREADY_APPLIED', 'INVALID_MESSAGE_ID'],
    },
  },
  /**
   * Rejects a classification suggestion and marks it as invalid for future reference.
   * Gets: suggestion id from path, optional feedback reason in body.
   * Returns: { success: true }.
   * Side Effects: Updates suggestion status to 'REJECTED', logs to activity_logs with rejection reason.
   * Error Codes: SUGGESTION_EXPIRED.
   */
  rejectSuggestion: {
    method: 'POST',
    path: '/v1/classify/suggestions/:id/reject',
    pathParams: z.object({ id: z.string() }),
    body: ClassificationRejectSuggestionRequestSchema.optional(),
    responses: {
      200: RejectClassificationSuggestionResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Reject a classification suggestion',
      description: 'Reject a suggestion and record feedback for model refinement.',
      errorCodes: ['SUGGESTION_EXPIRED'],
    },
  },
  /**
   * Excludes a classification suggestion AND suppresses the same sender+label
   * combo from future scans by writing a sender_exclusion_hints row. Used by
   * the Cleanup ribbon's "Exclude" row action (#296).
   *
   * Gets: suggestion id from path. Body is intentionally empty.
   * Returns: { ok: true, suggestion_id, excluded_at }.
   * Side Effects: sets classification_suggestions.user_action='excluded',
   *   excluded_at=now(); upserts sender_exclusion_hints (one row per label,
   *   or one row with label='*' when suggestion.labels is empty); writes an
   *   activity_logs entry with kind='suggestion.excluded'.
   * Error Codes: NOT_FOUND, SUGGESTION_ALREADY_PROCESSED.
   */
  excludeSuggestion: {
    method: 'POST',
    path: '/v1/classify/suggestions/:id/exclude',
    pathParams: z.object({ id: z.string().uuid() }),
    body: ClassificationExcludeSuggestionRequestSchema,
    responses: {
      200: ClassificationExcludeSuggestionResponseSchema,
      ...defaultErrorResponses,
      // Custom 409 body carries a `reason` discriminator so the frontend can
      // render `already_applied` vs `already_excluded` distinctly. Spread
      // `defaultErrorResponses` first so this override wins.
      409: ClassificationExcludeConflictResponseSchema,
    },
    metadata: {
      auth: true,
      summary: 'Exclude a suggestion + suppress sender+label in future scans',
      description:
        'Marks the suggestion as excluded (user_action=excluded), writes a sender_exclusion_hints row, and logs activity. Returns 409 with reason=already_applied or already_excluded if the suggestion is no longer pending.',
      errorCodes: ['NOT_FOUND', 'SUGGESTION_ALREADY_PROCESSED'],
    },
  },
  /**
   * Reassigns a suggestion to a different track without applying it.
   * Gets: suggestion id from path, new track in body.
   * Returns: { suggestion_id, old_track, new_track }.
   * Side Effects: Updates suggestion track and actions in DB. Does not execute Gmail actions.
   * Error Codes: SUGGESTION_EXPIRED, BAD_REQUEST.
   */
  reassignTrack: {
    method: 'POST',
    path: '/v1/classify/suggestions/:id/reassign',
    pathParams: z.object({ id: z.string() }),
    body: ReassignTrackRequestSchema,
    responses: {
      200: ReassignTrackResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Reassign suggestion to a different track',
      description: 'Updates the suggestion track and associated actions without executing Gmail actions.',
      errorCodes: ['SUGGESTION_EXPIRED', 'BAD_REQUEST'],
    },
  },
  /**
   * Reassigns pending suggestions that exactly match an already-corrected
   * anchor's sender and subject. This updates the Cleanup plan only; it does
   * not execute Gmail actions.
   */
  batchReassignTrack: {
    method: 'POST',
    path: '/v1/classify/suggestions/batch-reassign-track',
    body: BatchReassignTrackRequestSchema,
    responses: {
      200: BatchReassignTrackResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Reassign matching suggestions to another track',
      description: 'Updates pending suggestions that exactly match the anchor sender and normalized subject. Gmail is unchanged until suggestions are applied.',
      errorCodes: ['NOT_FOUND', 'VALIDATION_ERROR'],
    },
  },
  /**
   * Recalculates classification suggestion based on updated rules.
   * Gets: suggestion id from path.
   * Returns: { changed: boolean, old_track, new_track, old_confidence, new_confidence, ... }.
   * Side Effects: Re-evaluates the suggestion using current classification rules. Does not apply changes.
   * Error Codes: SUGGESTION_EXPIRED, SUGGESTION_APPLIED, INVALID_MESSAGE_ID.
   */
  reanalyzeSuggestion: {
    method: 'POST',
    path: '/v1/classify/suggestions/:id/reanalyze',
    pathParams: z.object({ id: z.string() }),
    body: z.object({}).optional(),
    responses: {
      200: ReanalyzeClassificationSuggestionResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Recalculate suggestion based on updated rules',
      description: 'Reanalyze a pending classification suggestion using current rules without modifying the original.',
      errorCodes: ['SUGGESTION_EXPIRED', 'SUGGESTION_APPLIED', 'INVALID_MESSAGE_ID'],
    },
  },
  /**
   * Re-runs classification for all pending suggestions for the user.
   * Gets: none. Returns: batch summary with totals and change counts.
   * Side Effects: Updates suggestion records with refreshed tracks/labels/actions/signals.
   */
  reanalyzeAllSuggestions: {
    method: 'POST',
    path: '/v1/classify/suggestions/reanalyze-all',
    body: z.object({}).optional(),
    responses: {
      200: ReanalyzeAllSuggestionsResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Reanalyze all pending suggestions (POST)',
      description: 'Reprocesses all pending suggestions using current rules and returns a batch summary of changes.',
    },
  },

  /**
   * GET variant of reanalyzeAllSuggestions - same behavior, callable from purebox.ai domain.
   * Accessible: GET /v1/classify/suggestions/reanalyze-all
   * From: purebox.ai (auto-proxied to api.purebox.ai via next.config.mjs)
   */
  reanalyzeAllSuggestionsGet: {
    method: 'GET',
    path: '/v1/classify/suggestions/reanalyze-all',
    responses: {
      200: ReanalyzeAllSuggestionsResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Reanalyze all pending suggestions (GET)',
      description: 'Idempotent GET variant: reprocesses all pending suggestions using current rules and returns a batch summary of changes. Same behavior as POST.',
    },
  },

  /**
   * DEV-ONLY. Re-runs classification for the latest scan's persisted free-sample target IDs,
   * capped at 100 emails. The action never reads the whole cached mailbox or Gmail.
   * Gets: none. Returns: { started, queued, already_running, started_at } immediately.
   * Side Effects: Starts a fire-and-forget background loop that replaces each message's
   * pending/low_confidence suggestion. Applied and rejected suggestions are left alone.
   * Error Codes: 404 in production - the endpoint is gated off outside development.
   */
  reclassifyAllEmails: {
    method: 'POST',
    path: '/v1/classify/emails/reclassify-all',
    body: z.object({}).optional(),
    responses: {
      200: ReclassifyAllEmailsResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Reclassify latest sample (dev-only)',
      description: 'Dev-only. Starts a background run for the latest scan sample, capped at 100 persisted targets, and returns immediately. Returns 404 in production.',
    },
  },

  /**
   * Creates a server-side batch apply job for a set of suggestions.
   * The job runs server-side - browser can be closed after creation.
   * Body: { track?, suggestion_ids?, label?, search?, confidence_floor? }
   * When `suggestion_ids` is omitted, the cohort is resolved server-side
   * from `track` + `label` + `search` + `confidence_floor` (#651), the same
   * way `listSuggestions` resolves its listing.
   * Returns: { job_id, total_suggestions }.
   * Returns 409 if user already has an active apply job.
   */
  batchApply: {
    method: 'POST',
    path: '/v1/classify/suggestions/batch-apply',
    body: BatchApplyRequestSchema,
    responses: {
      200: BatchApplyResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Start a server-side batch apply job',
      description: 'Atomically snapshots user-owned pending suggestions and creates or replays an idempotent apply job. Returns immediately; safe to close browser.',
      errorCodes: ['IDEMPOTENCY_KEY_CONFLICT'],
    },
  },
  /**
   * Synchronously apply a small cluster of pending suggestions that share
   * a sender or rationale (#350). Each suggestion is applied as its own
   * atomic Gmail action; per-id failures do NOT abort the batch, and the
   * response carries structured per-id results so the frontend can
   * revert only the failing IDs in its optimistic UI.
   */
  applyCluster: {
    method: 'POST',
    path: '/v1/classify/suggestions/cluster-apply',
    body: ApplyClusterRequestSchema,
    responses: {
      200: ApplyClusterResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Apply a cluster of suggestions in one request',
      description: 'Synchronously applies up to CLUSTER_APPLY_MAX_SUGGESTIONS suggestions sharing a sender or rationale. Per-id failures are reported in `results` and do not abort the batch.',
      errorCodes: ['BAD_REQUEST', 'GMAIL_TOKEN_MISSING', 'GMAIL_TOKEN_EXPIRED'],
    },
  },
  /**
   * Returns the active (PENDING or IN_PROGRESS) apply job for the user, or null.
   * Used for progress polling and "returning user" detection.
   */
  getActiveApplyJob: {
    method: 'GET',
    path: '/v1/classify/apply-jobs/active',
    responses: {
      200: GetActiveApplyJobResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Get active apply job progress',
      description: 'Returns the current active apply job (PENDING or IN_PROGRESS) with progress counters, or null.',
    },
  },
  /**
   * Returns full apply job details by ID including failed_suggestion_ids.
   */
  getApplyJob: {
    method: 'GET',
    path: '/v1/classify/apply-jobs/:id',
    pathParams: z.object({ id: z.string().uuid() }),
    responses: {
      200: GetApplyJobResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Get apply job by ID',
      description: 'Returns full apply job details including failed suggestion IDs.',
      errorCodes: ['NOT_FOUND'],
    },
  },
  /**
   * Records user feedback (accept/reject/override) on a classification suggestion.
   * Gets: suggestion id from path, verdict + optional reason/override in body.
   * Returns: { suggestion_id, verdict, feedback_at }.
   * Side Effects: Updates suggestion feedback columns, logs activity. Returns 409 if already recorded.
   *
   * Note (#283 Slice 2): the `reject_reason` and `reject_reason_text` body
   * fields are not captured by any UI and are reserved for backward
   * compatibility / future reuse. The override path used by the suggestion
   * row Move-to cluster sends `verdict: 'overridden', override_track: <Track>`.
   */
  recordFeedback: {
    method: 'POST',
    path: '/v1/classify/suggestions/:id/feedback',
    pathParams: z.object({ id: z.string().uuid() }),
    body: SuggestionFeedbackRequestSchema,
    responses: {
      200: SuggestionFeedbackResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Record user feedback on a classification suggestion',
      description: 'Accept, reject, or override a suggestion. Reject requires a reason. Override requires a target track.',
      errorCodes: ['NOT_FOUND', 'CONFLICT'],
    },
  },
})
